"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  entriesToFiles,
  pointInRect,
  type NativeDropDetail,
} from "@/lib/native-file-drop";
import { namePastedImages } from "./pasted-image-name";

/**
 * All of the composer's file-intake affordances in one place: HTML5 drag-and-drop,
 * clipboard paste, and the desktop shell's native drop bridge. Every path funnels
 * into the SAME `handleFiles` (the + button's attach → upload lane), so there is one
 * pipeline, not three.
 *
 * - Browser: `dragHandlers` + `onPaste` do the work; HTML5 DnD delivers files directly.
 * - Desktop app: WKWebView never populates HTML5 file drops, so `file_drop.rs` reads
 *   the bytes natively and dispatches `vestra:file-drop` / `vestra:file-drag`, which
 *   the effect below converts back into Files. The listener is inert in a plain browser
 *   (those events never fire there).
 */
export interface ComposerFileDrop {
  isDragOver: boolean;
  dragHandlers: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  onPaste: (e: React.ClipboardEvent) => void;
}

interface Args {
  /** The shared attach → upload lane (from useAttachmentHandler). */
  handleFiles: (files: File[]) => void;
  /** Surface a rejection using the composer's existing error line. */
  setImageError: (message: string) => void;
  /** The composer root, hit-tested against the native drop point. */
  rootRef: RefObject<HTMLElement | null>;
}

export function useComposerFileDrop({
  handleFiles,
  setImageError,
  rootRef,
}: Args): ComposerFileDrop {
  const [isDragOver, setIsDragOver] = useState(false);
  // Counter, not a boolean: dragenter/leave fire for every child element, so a naive
  // toggle flickers as the cursor crosses the textarea, buttons, etc.
  const dragCounterRef = useRef(0);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) setIsDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    // Required for `drop` to fire at all.
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleFiles(files);
    },
    [handleFiles]
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      // Clipboard screenshots arrive as a generic `image.png` — rename them
      // here, where the user's local clock is truthful (pasted-image-name.ts).
      const files = namePastedImages(Array.from(e.clipboardData?.files ?? []));
      if (files.length > 0) {
        // Only intercept when the clipboard carries files (a pasted screenshot);
        // plain-text paste falls through to the textarea untouched.
        e.preventDefault();
        handleFiles(files);
      }
    },
    [handleFiles]
  );

  useEffect(() => {
    const onNativeDrag = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { phase?: string }
        | undefined;
      setIsDragOver(detail?.phase === "enter");
    };
    const onNativeDrop = (e: Event) => {
      const detail = (e as CustomEvent).detail as NativeDropDetail | undefined;
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (!detail) return;
      // The native handler is window-global; scope to the composer so a drop on the
      // files-browser pane (or anywhere else) isn't hijacked into the message input.
      const dpr = window.devicePixelRatio || 1;
      const rect = rootRef.current?.getBoundingClientRect();
      if (!pointInRect(detail.x / dpr, detail.y / dpr, rect)) return;
      const files = entriesToFiles(detail.files ?? []);
      if (files.length > 0) handleFiles(files);
      for (const err of detail.errors ?? []) {
        if (err.reason === "too_large" && typeof err.sizeBytes === "number") {
          setImageError(
            `${err.name} is too large (${(err.sizeBytes / 1024 / 1024).toFixed(
              1
            )}MB)`
          );
        }
      }
    };
    window.addEventListener("vestra:file-drag", onNativeDrag);
    window.addEventListener("vestra:file-drop", onNativeDrop);
    return () => {
      window.removeEventListener("vestra:file-drag", onNativeDrag);
      window.removeEventListener("vestra:file-drop", onNativeDrop);
    };
  }, [handleFiles, setImageError, rootRef]);

  return {
    isDragOver,
    dragHandlers: { onDragEnter, onDragLeave, onDragOver, onDrop },
    onPaste,
  };
}
